/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { platformMcpRecordInstallIntent } from "../funcs/platformMcpRecordInstallIntent.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { PlatformMCPOnboardingState } from "../models/components/platformmcponboardingstate.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RecordPlatformMCPInstallIntentRequest,
  RecordPlatformMCPInstallIntentSecurity,
} from "../models/operations/recordplatformmcpinstallintent.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RecordPlatformMCPInstallIntentMutationVariables = {
  request: RecordPlatformMCPInstallIntentRequest;
  security?: RecordPlatformMCPInstallIntentSecurity | undefined;
  options?: RequestOptions;
};

export type RecordPlatformMCPInstallIntentMutationData =
  PlatformMCPOnboardingState;

export type RecordPlatformMCPInstallIntentMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * recordInstallIntent platformMcp
 *
 * @remarks
 * Record a selected manual-install client family for the current user's Platform MCP workflow.
 */
export function useRecordPlatformMCPInstallIntentMutation(
  options?: MutationHookOptions<
    RecordPlatformMCPInstallIntentMutationData,
    RecordPlatformMCPInstallIntentMutationError,
    RecordPlatformMCPInstallIntentMutationVariables
  >,
): UseMutationResult<
  RecordPlatformMCPInstallIntentMutationData,
  RecordPlatformMCPInstallIntentMutationError,
  RecordPlatformMCPInstallIntentMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRecordPlatformMCPInstallIntentMutation(client, options),
    ...options,
  });
}

export function mutationKeyRecordPlatformMCPInstallIntent(): MutationKey {
  return ["@gram/client", "platformMcp", "recordInstallIntent"];
}

export function buildRecordPlatformMCPInstallIntentMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RecordPlatformMCPInstallIntentMutationVariables,
  ) => Promise<RecordPlatformMCPInstallIntentMutationData>;
} {
  return {
    mutationKey: mutationKeyRecordPlatformMCPInstallIntent(),
    mutationFn: function recordPlatformMCPInstallIntentMutationFn({
      request,
      security,
      options,
    }): Promise<RecordPlatformMCPInstallIntentMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(platformMcpRecordInstallIntent(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
